// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Per-station slide-in drawer, hash-linkable via #s=<id>. The panel is BUILT on
// open and REMOVED from the DOM on close — never parked at translateX(100%),
// which on iOS Safari stays a real, page-widening box (factory memory).

import type { ClimateData, Station } from './types';
import { loadStationDetail } from './data';
import { fmtSigned, fmtRate, fmtTemp, fmtNum, fmtDate, STATE_NAMES, esc, MONTHS } from './format';
import { anomalyColor } from './color';
import { stripes } from './charts';
import { gloss } from './glossary';

let host: HTMLDivElement | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;

export function closeDrawer(updateHash = true): void {
  if (host) { host.remove(); host = null; }
  if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
  if (updateHash && location.hash.includes('s=')) {
    const p = new URLSearchParams(location.hash.slice(1));
    p.delete('s');
    history.replaceState(null, '', '#' + p.toString());
  }
}

function rankOf(data: ClimateData, s: Station): { rank: number; total: number } | null {
  if (s.rateMean == null) return null;
  const rated = data.stations.filter((x) => x.rateMean != null).sort((a, b) => (b.rateMean as number) - (a.rateMean as number));
  return { rank: rated.findIndex((x) => x.id === s.id) + 1, total: rated.length };
}

function tile(label: string, value: string, sub = ''): string {
  return `<div class="d-tile"><span class="d-tile-l">${label}</span><span class="d-tile-v">${value}</span>${sub ? `<span class="d-tile-s">${sub}</span>` : ''}</div>`;
}

export async function openDrawer(data: ClimateData, id: string): Promise<void> {
  const s = data.stations.find((x) => x.id === id);
  if (!s) return;
  closeDrawer(false);

  const p = new URLSearchParams(location.hash.slice(1));
  p.set('s', id);
  history.replaceState(null, '', '#' + p.toString());

  host = document.createElement('div');
  host.className = 'drawer-host';
  const rk = rankOf(data, s);
  const stripeData = s.annual.map((r) => ({ y: r.y, a: r.aMean }));
  const nat = data.meta.nationalRate;

  host.innerHTML = `
    <div class="drawer-overlay" data-close></div>
    <aside class="drawer" role="dialog" aria-modal="true" aria-label="${esc(s.name)} details">
      <div class="drawer-head">
        <div>
          <h2>${esc(s.name)}</h2>
          <p class="drawer-sub">${esc(STATE_NAMES[s.state] ?? s.state)} · ${s.elevation != null ? fmtNum(s.elevation) + ' m elevation · ' : ''}record ${s.startYear}–${s.endYear}</p>
        </div>
        <button class="drawer-close" data-close aria-label="Close">✕</button>
      </div>
      <div class="drawer-body">
        <div class="d-tiles">
          ${tile(gloss('rate', 'Warming rate'), fmtRate(s.rateMean), rk ? `#${rk.rank} of ${rk.total} · national ${fmtRate(nat)}` : 'no long trend')}
          ${tile('Total warming', s.totalMean != null ? fmtSigned(s.totalMean) : '—', `over ${s.startYear}–${s.endYear}`)}
          ${tile(gloss('tmin', 'Nights'), fmtRate(s.rateMin), 'overnight low trend')}
          ${tile(gloss('tmax', 'Days'), fmtRate(s.rateMax), 'daytime high trend')}
          ${tile('Recent anomaly', s.recentAnom != null ? fmtSigned(s.recentAnom) : '—', 'last 10 years vs 1961–1990')}
          ${tile(gloss('hotday', 'Hot days now'), s.latestH35 != null ? fmtNum(s.latestH35) + ' / yr' : '—', s.trendH35 != null ? `${fmtSigned(s.trendH35, 1, '')} days/decade` : '')}
          ${tile('Hottest day', s.recHigh ? fmtTemp(s.recHigh.t) : '—', s.recHigh ? fmtDate(s.recHigh.date) : '')}
          ${tile('Coldest night', s.recLow ? fmtTemp(s.recLow.t) : '—', s.recLow ? fmtDate(s.recLow.date) : '')}
        </div>

        <h3 class="d-h3">Warming stripes <span class="d-h3-sub">annual ${gloss('anomaly', 'anomaly')} vs 1961–1990</span></h3>
        <div class="d-stripes">${s.hasBaseline ? stripes(stripeData, { w: 760, h: 78 }) : '<p class="muted">No 1961–1990 baseline at this station, so anomalies aren\'t shown.</p>'}</div>
        <div class="d-stripe-axis"><span>${s.startYear}</span><span>${s.endYear}</span></div>

        <h3 class="d-h3">Month-by-month <span class="d-h3-sub">how each month warmed — grid reddens over time</span></h3>
        <div class="d-heat" data-heat>Loading…</div>
      </div>
    </aside>`;

  document.body.appendChild(host);
  host.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => closeDrawer()));
  escHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDrawer(); };
  document.addEventListener('keydown', escHandler);
  // Entrance is a CSS @keyframes whose RESTING state is on-screen — so if the
  // animation is throttled (backgrounded tab) the panel still ends up visible,
  // unlike a transition that would stick at its off-screen start.

  // Lazy heatmap of year×month anomalies.
  const heatEl = host.querySelector('[data-heat]') as HTMLElement;
  try {
    const detail = await loadStationDetail(id);
    if (host && heatEl) heatEl.innerHTML = renderMonthHeat(detail.ym);
  } catch {
    if (heatEl) heatEl.innerHTML = '<p class="muted">Monthly detail unavailable.</p>';
  }
}

function renderMonthHeat(ym: Array<[number, Array<number | null>]>): string {
  const rows = ym.filter((r) => r[1].some((v) => v != null));
  if (!rows.length) return '<p class="muted">No monthly data.</p>';
  const years = rows.map((r) => r[0]);
  const y0 = years[0], y1 = years[years.length - 1];
  const w = 760, top = 16, left = 30, cellH = Math.max(2, Math.min(6, 520 / rows.length));
  const cellW = (w - left) / 12;
  let g = '';
  for (let mi = 0; mi < 12; mi++) g += `<text x="${(left + mi * cellW + cellW / 2).toFixed(1)}" y="11" class="heat-lbl" text-anchor="middle">${MONTHS[mi][0]}</text>`;
  rows.forEach((r, ri) => {
    const y = top + ri * cellH;
    r[1].forEach((v, mi) => {
      g += `<rect x="${(left + mi * cellW).toFixed(1)}" y="${y.toFixed(1)}" width="${(cellW + 0.5).toFixed(1)}" height="${(cellH + 0.5).toFixed(1)}" fill="${anomalyColor(v, 2)}" data-tip="${r[0]} ${MONTHS[mi]}: ${v == null ? 'no data' : (v > 0 ? '+' : '') + v.toFixed(1) + ' °C'}"/>`;
    });
  });
  const labelYears = [y0, Math.round((y0 + y1) / 2), y1];
  labelYears.forEach((yr) => {
    const ri = years.indexOf(yr);
    if (ri >= 0) g += `<text x="2" y="${(top + ri * cellH + cellH).toFixed(1)}" class="heat-lbl">${yr}</text>`;
  });
  const h = top + rows.length * cellH + 4;
  return `<svg viewBox="0 0 ${w} ${h}" class="heat-svg" role="img" aria-label="Month by year anomaly heatmap">${g}</svg>`;
}
